import { AttachedFiles, AttachedFilesRequest } from "legacyComponents/FileUploadContainer.types";
import { type LineItemType } from "legacyComponents/LineItemContainer.types";
import moment, { type Moment } from "moment";

import { DocumentInstanceType, type ProposalGroupTypes } from "types/enum";

import yup from "utilities/form/yup";

import { type EntityRecordLinkInfo } from "commonComponents/entity/entityRecord/EntityRecord.types";
import {
    isLineItemGroupRow,
    isLineItemRow,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import {
    isProposalSubCategory,
    maxCategoryTitleLength,
} from "commonComponents/utilities/ProposalBaseLineItemContainer/WorksheetLineItemContainer.utility";

import {
    type ApprovalDetailsForDisplay as ApprovalDetailsForDisplayApiV2,
    type BTFileSystem,
    ProposalColumnTypes,
    type ProposalDisplayConfigProposalLayout,
    type ProposalForDisplayFormatItemsItem,
    type ProposalPrintoutTypes,
    type ProposalSignature as ProposalSignatureApiV2,
    type ProposalStatus,
} from "handlers";

import {
    getDefaultProposalCategoryAttachedFiles,
    getItemTitleValidationForAllowances,
    type LeadProposalLineItem,
    OptionMode,
    type ProposalCategory,
    type ProposalSubCategory,
    UnassignedCostGroupId,
    type WorksheetLineItem,
} from "entity/estimate/common/estimate.common.types";
import { getLineItemEstimatesSchema } from "entity/estimate/EstimateLineItemContainer/LineItemDetails/LineItemDetails.api.types";

export enum ProposalTypes {
    JobProposal = 0,
    LeadProposal = 1,
    LeadProposalTemplate = 2,
}

export enum ProposalEntityNames {
    JobProposal = "Proposal",
}

export type ProposalFormActions =
    | undefined
    | "save"
    | "saveAndClose"
    | "delete"
    | "releaseToOwner"
    | "unrelease"
    | "approve"
    | "decline"
    | "resend"
    | "print"
    | "reset"
    | "moveItems"
    | "recategorizeFormat";

export interface IProposalFormValues {
    title: string;
    approvalDeadline: Date | null;
    introductionText: string;
    closingText: string;
    attachments: AttachedFilesRequest;
    requireSignatures: boolean;
    requiredSignatureUsers: number[];
    requiredSignatureId?: number;
    columnsToDisplay: ProposalColumnTypes[];
    headerLayout: ProposalHeaderLayouts;
    bodyLayout: ProposalBodyLayouts;
    showOwnerContactInfo: boolean;
    showAddress: boolean;
    showPrintoutInfo: boolean;
    printoutType: ProposalPrintoutTypes;
    includeSpecs: boolean;
    formatItems: readonly ProposalForDisplayFormatItemsItem[];
    newCategoryId: number;
    files: readonly BTFileSystem[]; // This files field is for holding files as they update without triggering a re-load to allow for new attachments on the proposal to show.
    proposalLayout?: ProposalDisplayConfigProposalLayout;
}

export interface IProposalSignatureObject {
    signaturePath?: string;
    signedById: number;
    signedByDate?: Moment;
    signedByName: string;
    approvalType: ProposalSignatureStatus;
    proposalRequestOwnerName: string;
    proposalRequestOrgLinkId: number;
    reason: string;
    isRequired: number;
}

export class ProposalSignatureObject implements IProposalSignatureObject {
    constructor(data: IProposalSignatureObject) {
        this.signaturePath = data.signaturePath;
        this.signedById = data.signedById;
        this.signedByDate = data.signedByDate ? moment(data.signedByDate) : undefined;
        this.signedByName = data.signedByName;
        this.approvalType = data.approvalType;
        this.proposalRequestOwnerName = data.proposalRequestOwnerName || "Client";
        this.proposalRequestOrgLinkId = data.proposalRequestOrgLinkId;
        this.reason = data.reason;
        this.isRequired = data.isRequired;
    }

    static fromProposalSignatureApiV2(data: ProposalSignatureApiV2): ProposalSignatureObject {
        return {
            signaturePath: data.signaturePath ?? undefined,
            signedById: data.signedById ?? 0,
            signedByDate: data.signedByDate ? moment(data.signedByDate) : undefined,
            signedByName: data.signedByName ?? "",
            approvalType: data.approvalType ?? 0,
            proposalRequestOwnerName: data.proposalRequestOwnerName ?? "",
            proposalRequestOrgLinkId: data.proposalRequestOrgLinkId ?? 0,
            reason: data.reason ?? "",
            isRequired: data.isRequired ?? 0,
        };
    }

    signaturePath?: string;
    signedById: number;
    signedByDate?: Moment;
    signedByName: string;
    approvalType: ProposalSignatureStatus;
    proposalRequestOwnerName: string;
    proposalRequestOrgLinkId: number;
    reason: string;
    isRequired: number;
}

export enum ProposalSignatureStatus {
    Pending = 0,
    Approved = 1,
    Declined = 2,
    ManuallyApproved = 3,
    ManuallyDeclined = 4,
    UnreleasedApproved = 5,
    UnreleasedDeclined = 6,
}

export class BuilderInfo {
    constructor(data: any) {
        this.logo = data.logo;
        this.companyName = data.companyName;
        this.phone = data.phone;
        this.fax = data.fax;
        this.includeNameOnPrintouts = data.includeNameOnPrintouts;
        this.shouldShowAddressOnPrintouts = data.shouldShowAddressOnPrintouts;
        this.additionalFields = data.additionalFields;
    }
    logo: string;
    companyName: string;
    phone: string;
    fax: string;
    includeNameOnPrintouts: boolean;
    shouldShowAddressOnPrintouts: boolean;
    additionalFields: string[];
}

export class ProposalStatusObject {
    constructor(data: any) {
        this.status = data.status;
        this.user = data.updatedByName ?? data.statusText;
        this.isExpired = data.isExpired;

        const updatedDate = data.updatedDate ?? data.date;
        this.date = updatedDate as Date | null;
    }

    static fromApprovalDetails(data: ApprovalDetailsForDisplayApiV2): ProposalStatusObject {
        return {
            status: data.status,
            user: data.updatedByName,
            isExpired: data.isExpired,
            date: data.updatedDate,
        };
    }

    status: ProposalStatus;
    user: string;
    date: Date | null;
    isExpired: boolean;
}

export interface IUnreleasedProposalLineItemSaveRequest {
    proposalFormatItemId: number;
}

export interface IUnreleasedProposalGroupSaveRequest {
    proposalFormatItemId: number;
    isExpanded: boolean;
    optionMode?: OptionMode;
    /** only included for the unassigned group */
    lineItems?: IUnreleasedProposalLineItemSaveRequest[];
}

export interface IUnreleasedProposalSaveRequest {
    title: string;
    approvalDeadline: Date | null;
    introductionText: string;
    closingText: string;
    columnsToDisplay: number[];
    /** both the root groups and subgroups */
    categories: IUnreleasedProposalGroupSaveRequest[];
    formatOptions: IProposalFormatOptions;
    requireSignatures: boolean;
    requiredSignatureUsers: number[];
    requiredSignatureId?: number;
}
export interface IProposalFormatOptions {
    header: ProposalHeaderLayouts;
    body: ProposalBodyLayouts;
    showOwnerContactInfo: boolean;
    showAddress: boolean;
    showPrintoutInfo: boolean;
    printoutType: ProposalPrintoutType;
    hasSingleSelectCostTypes: boolean;
    includeSpecs: boolean;
    proposalLayout?: ProposalDisplayConfigProposalLayout;
}

export enum ProposalHeaderLayouts {
    Centered = 1,
    Split = 2,
}

export enum ProposalBodyLayouts {
    Column = 1,
    MultiLine = 2,
    None = 3,
}

export enum ProposalPrintoutType {
    Line = 1,
    Stacked = 2,
}

export const proposalFormatLineItemValidationSchema = (lineItemType: LineItemType) =>
    getLineItemEstimatesSchema((schema) => {
        return {
            ...schema,
            unitPrice: yup.number().label("Unit Price").nullable(),
            itemTitle: getItemTitleValidationForAllowances(lineItemType, schema.itemTitle),
        };
    });

export const ProposalCategoryValidationSchema = (
    groupEntityName: string,
    subgroupEntityName: string,
    category: ProposalCategory,
) => {
    const isSubgroup = isProposalSubCategory(category);
    const groupLabel = isSubgroup ? subgroupEntityName : groupEntityName;
    return yup.object<ProposalCategory>({
        id: yup.number(),
        parentId: yup.number().nullable(),
        title: yup.string().required().max(maxCategoryTitleLength).label(`${groupLabel} Title`),
        description: yup.string().nullable().max(2000).label(`${groupLabel} Description`),
        attachedFiles: yup.mixed(),
        items: yup.array(),
        optionMode: yup.number(),
        // we don't really care about these ones
        aggregates: yup.mixed(),
        attachments: yup.mixed(),
        builderId: yup.mixed(),
        costCategoryId: yup.mixed(),
        displayOrder: yup.mixed(),
        documentInstanceType: yup.mixed(),
        field: yup.mixed(),
        isExpanded: yup.mixed(),
        isSelected: yup.mixed(),
        jobId: yup.mixed(),
        leadId: yup.mixed(),
        name: yup.mixed(),
        value: yup.mixed(),
        openAddAttachFiles: yup.boolean(),
        openCreateDocument: yup.boolean(),
        mountFileComponent: yup.boolean(),
        files: yup.mixed(),
        viewAllFiles: yup.boolean(),
        entityRecordLinks: yup.array().of(yup.mixed()),
    });
};

export const getUnassignedCategory = (builderId: number) => {
    // JobId is hardcoded to -1 here, since the unassigned category isn't saved and can't use attachments the jobId doesn't matter.
    const unassignedGroup = getDefaultProposalCategory(-1, builderId, UnassignedCostGroupId);
    unassignedGroup.title = "Unassigned";
    return unassignedGroup;
};

export const getDefaultProposalCategory = (
    jobId: number,
    builderId: number,
    newCategoryId: number,
): ProposalCategory => {
    const newCategory: ProposalCategory = {
        items: [],
        id: newCategoryId,
        _isInEdit: false,
        displayOrder: 0,
        openCreateDocument: false,
        builderId: builderId,
        openAddAttachFiles: false,
        mountFileComponent: false,
        field: "",
        value: "",
        description: null,
        attachedFiles: new AttachedFiles({ ...getDefaultProposalCategoryAttachedFiles(jobId) }),
        title: "",
        isSelected: false,
        isExpanded: true,
        attachments: new AttachedFilesRequest(),
        costCategoryId: null,
        documentInstanceType: DocumentInstanceType.ProposalFormatItem,
        name: "Category",
        aggregates: "",
        jobId: jobId,
        leadId: 0,
        files: [],
        viewAllFiles: false,
        autoFocusedField: "title",
        optionMode: OptionMode.Required,
        entityRecordLinks: [],
    };

    return newCategory;
};

export const getDefaultProposalSubCategory = (
    jobId: number,
    builderId: number,
    newCategoryId: number,
    parentId: number,
): ProposalSubCategory => {
    const newSubCategory = getDefaultProposalCategory(
        jobId,
        builderId,
        newCategoryId,
    ) as ProposalSubCategory;

    newSubCategory.parentId = parentId;
    newSubCategory.name = "Sub Category";

    return newSubCategory;
};

class ProposalCategorySaveRequest {
    constructor(category: ProposalCategory, displayOrder: number) {
        // for new categories, they will have a negative number id.
        this.id = category.id;
        this.costCategoryId = category.costCategoryId;
        this.title = category.title;
        this.description = category.description;
        this.lineItems = [];
        this.attachments = category.attachments;
        this.displayOrder = displayOrder;
        this.isExpanded = category.isExpanded;
        this.optionMode = category.optionMode;
        this.parentId = category.parentId;
        this.entityRecordLinks = category.entityRecordLinks;
    }

    id: number | null;
    costCategoryId: number | null;
    title: string;
    description: string | null;
    lineItems: ProposalLineItemSaveRequest[];
    attachments: AttachedFilesRequest;
    displayOrder: number;
    isExpanded: boolean;
    optionMode?: number;
    // If parentId is defined, its a sub category
    parentId?: number;
    entityRecordLinks: EntityRecordLinkInfo[] = [];
}

class ProposalLineItemSaveRequest {
    constructor(lineItem: WorksheetLineItem | LeadProposalLineItem, displayOrder: number) {
        this.id = lineItem.id;
        this.formatId = lineItem.formatId!;
        this.costCategoryFormatItemId = lineItem.costCategoryFormatId;
        this.displayOrder = displayOrder;
    }

    id: number;
    formatId: number;
    costCategoryFormatItemId: number | null;
    displayOrder: number;
    isExpanded: boolean;
}

export class ProposalFormatGroupSaveRequest {
    constructor(proposalId: number, category: ProposalCategory) {
        this.proposalId = proposalId;
        this.formatGroup = new ProposalCategorySaveRequest(category, category.displayOrder);
    }
    proposalId: number;
    formatGroup: ProposalCategorySaveRequest;
}

export class ProposalFormatGroupBatchUpdateRequest {
    constructor(proposalId: number, categories: ProposalCategory[]) {
        this.proposalId = proposalId;
        this.formatGroups = categories.map(
            (c) => new ProposalCategorySaveRequest(c, c.displayOrder),
        );
    }
    proposalId: number;
    formatGroups: ProposalCategorySaveRequest[];
}

const getLineItems = (category: ProposalCategory): ProposalLineItemSaveRequest[] => {
    const requests: ProposalLineItemSaveRequest[] = [];
    category.items.forEach((item, i) => {
        if (isLineItemRow(item)) {
            const lineItemRequest = new ProposalLineItemSaveRequest(item, i);
            lineItemRequest.costCategoryFormatItemId = category.id;
            lineItemRequest.isExpanded = category.isExpanded;
            requests.push(lineItemRequest);
        }

        if (isLineItemGroupRow(item) && item.items.length > 0) {
            const items = getLineItems(item);
            requests.push(...items);
        }
    });

    return requests;
};

export class ProposalFormatLineItemRequest {
    constructor(proposalId: number, categories: ProposalCategory[]) {
        this.proposalId = proposalId;
        this.lineItems = categories.flatMap((c) => getLineItems(c));
    }

    proposalId: number;
    lineItems: ProposalLineItemSaveRequest[];
}

export const getProposalFormatLineItemRequestFromLineItems = (
    worksheetId: number,
    lineItems: WorksheetLineItem[],
): ProposalFormatLineItemRequest => {
    const items = lineItems.map((item) => {
        const request = new ProposalLineItemSaveRequest(item, item.displayOrder!);
        request.costCategoryFormatItemId = item.parentId!;
        return request;
    });
    return { proposalId: worksheetId, lineItems: items };
};

export enum ProposalApprovalType {
    Approve,
    Decline,
}

export const enum RecategorizeType {
    ClearAll = 0,
    KeepFormatting = 1,
    PullFormattingFromLegacyGroups = 2,
    SortAlphanumerically = 3,
    ReformatByCostCode = 4,
    PullFormattingFromTakeoffAssemblies = 5,
}

export interface IRecategorizeProposalFormatRequest {
    /**
     * Deprecated, group type should be determined by the RecategorizeType if needed.
     * This property should be removed with cost code grouping on the proposal worksheet, as it's still needed for that one scenario.
     */
    newGroupType?: ProposalGroupTypes;
    recategorizeType: RecategorizeType;
}

export const setOfLineItemSpecificColumns: ReadonlySet<ProposalColumnTypes> = new Set([
    ProposalColumnTypes.CostCode,
    ProposalColumnTypes.Description,
    ProposalColumnTypes.QuantityAndUnit,
    ProposalColumnTypes.UnitCost,
    ProposalColumnTypes.BuilderCost,
    ProposalColumnTypes.Markup,
    ProposalColumnTypes.MarkupAmount,
    ProposalColumnTypes.UnitPrice,
    ProposalColumnTypes.OwnerPrice,
    ProposalColumnTypes.ItemTitle,
    ProposalColumnTypes.CostType,
    ProposalColumnTypes.TotalTax,
    ProposalColumnTypes.TaxableStatus,
]);

export const hasLineItemSpecificColumn = (lineItemColumnTypes: readonly ProposalColumnTypes[]) => {
    return lineItemColumnTypes.some((colType) => setOfLineItemSpecificColumns.has(colType));
};
